import {
  BatchRequestProperties,
  BatchIdSerializer,
  BatchRequestError,
  BatchItemProcessor,
  QueueItem,
  ItemProcessorResult,
  ItemExpirationProcessor,
  FailureCooldownProcessor,
  DefaultCooldown,
  CacheProperties
} from './batchRequestConstants';
import CacheStore from './cacheStore';

class BatchRequestProcessor {
  private completeItems: CacheStore;

  private requestQueue: Array<QueueItem> = [];

  private active = false;

  private maxRetryAttempts?: number;

  private getItemExpiration?: ItemExpirationProcessor;

  private batchSize: number;

  private processBatchWaitTime?: number;

  private getFailureCooldown?: FailureCooldownProcessor;

  private cacheProperties?: CacheProperties;

  private itemsProcessor: BatchItemProcessor;

  private itemsSerializer: BatchIdSerializer;

  constructor(
    itemsProcessor: BatchItemProcessor,
    itemsSerializer: BatchIdSerializer,
    properties: BatchRequestProperties
  ) {
    const {
      cacheProperties,
      processBatchWaitTime,
      batchSize,
      maxRetryAttempts,
      getItemExpiration,
      getFailureCooldown
    } = properties;
    const { useCache, expirationWindowMS } = cacheProperties;
    this.cacheProperties = cacheProperties;
    this.completeItems = new CacheStore(useCache, expirationWindowMS);
    this.processBatchWaitTime = processBatchWaitTime;
    this.batchSize = batchSize;
    this.maxRetryAttempts = maxRetryAttempts;
    this.getItemExpiration = getItemExpiration;
    this.getFailureCooldown = getFailureCooldown;
    this.itemsProcessor = itemsProcessor;
    this.itemsSerializer = itemsSerializer;
  }

  handleBatchResult(batch: Array<QueueItem>, error: BatchRequestError) {
    let minimumCooldown = 0;
    const currentDate = new Date().getTime();
    batch.forEach((request: QueueItem) => {
      if (this.completeItems.has(request.key, request.cacheProperties)) {
        const requestCompleteTime = new Date().getTime();
        request.resolve({
          ...this.completeItems.get(request.key, request.cacheProperties),
          ...{ performance: { duration: requestCompleteTime - request.startTime.getTime() } }
        });
      } else if (this.maxRetryAttempts && error !== BatchRequestError.unretriableFailure) {
        const itemCooldown = this.getFailureCooldown
          ? this.getFailureCooldown(request.retryAttempts)
          : DefaultCooldown;

        if (minimumCooldown > 0) {
          minimumCooldown = Math.min(minimumCooldown, itemCooldown);
        } else {
          minimumCooldown = itemCooldown;
        }

        if (++request.retryAttempts <= this.maxRetryAttempts) {
          request.queueAfter = currentDate + itemCooldown;
          // Put in front of the queue to make sure duplicate items
          // don't get processed without the cooldown time.
          this.requestQueue.unshift(request);
        } else {
          request.reject(BatchRequestError.maxAttemptsReached);
        }
      } else {
        console.debug(error, request);
        request.reject(error);
      }
    });

    if (minimumCooldown > 0) {
      setTimeout(() => this.processQueue(), minimumCooldown + this.processBatchWaitTime);
    }
    this.active = false;
    this.processQueue();
  }

  processQueue() {
    if (this.active) {
      return;
    }
    const batch: Array<QueueItem> = [];
    const batchKeys: Map<string, QueueItem> = new Map();
    const requeueRequests: Array<QueueItem> = [];
    const currentDate = new Date().getTime();

    while (batch.length < this.batchSize && this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      if (request.queueAfter > currentDate) {
        batchKeys.set(request.key, request);
        requeueRequests.push(request);
        continue;
      } else if (this.completeItems.has(request.key, request.cacheProperties)) {
        const requestCompleteTime = new Date().getTime();
        request.resolve({
          ...this.completeItems.get(request.key, request.cacheProperties),
          ...{ performance: { duration: requestCompleteTime - request.startTime.getTime() } }
        });
      } else if (batchKeys.has(request.key)) {
        // Requeue to make sure duplicate requests still get resolved once they're completed.
        requeueRequests.push(request);
      } else {
        batchKeys.set(request.key, request);
        batch.push(request);
      }
    }

    this.requestQueue.push(...requeueRequests);

    if (batch.length <= 0) {
      return;
    }

    this.active = true;

    this.itemsProcessor(batch).then(
      (data: ItemProcessorResult) => {
        Object.entries(data).forEach(([key, value]) => {
          const request = batchKeys.get(key);
          this.saveCompleteItem(key, value, request?.cacheProperties);
        });

        this.handleBatchResult(batch, BatchRequestError.processFailure);
      },
      error => {
        this.handleBatchResult(batch, error);
      }
    );
  }

  saveCompleteItem(key: string, data: any, cacheProperties?: CacheProperties) {
    this.completeItems.set(key, data, cacheProperties || this.cacheProperties);
    if (this.getItemExpiration) {
      setTimeout(() => {
        this.completeItems.delete(key);
      }, this.getItemExpiration(key));
    }
  }

  queueItem(itemId: number, key?: string, cacheProperties?: CacheProperties) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        key: key || this.itemsSerializer(itemId),
        itemId,
        retryAttempts: 0,
        queueAfter: 0,
        startTime: new Date(),
        cacheProperties: cacheProperties || this.cacheProperties,
        resolve,
        reject
      });
      setTimeout(() => this.processQueue(), this.processBatchWaitTime);
    });
  }

  invalidateItem(itemId: number, key?: string) {
    const storeKey = key || this.itemsSerializer(itemId);
    return this.completeItems.delete(storeKey);
  }

  clearCache() {
    this.completeItems.clear();
  }
}

export default BatchRequestProcessor;
